if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        if (document.readyState === 'complete') {
          setTimeout(() => this._storeOriginalGallerySlides(), 100);
        } else {
          window.addEventListener('load', () => {
            setTimeout(() => this._storeOriginalGallerySlides(), 100);
          }, { once: true });
        }

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      _storeOriginalGallerySlides() {
        const sectionId = this.dataset.section;
        const galleryUl = this.querySelector(`#Slider-Gallery-${sectionId}`);
        const thumbSlider = this.querySelector(`#Slider-Thumbnails-${sectionId}`);

        if (!galleryUl) return;

        if (!this._originalGallerySlides) {
          this._originalGallerySlides = Array.from(
            galleryUl.querySelectorAll('li[data-media-id]')
          ).map((li) => li.cloneNode(true));
        }

        if (!this._originalThumbnails) {
          this._originalThumbnails = thumbSlider
            ? Array.from(
                thumbSlider.querySelectorAll('.slider-item')
              ).map((el) => el.cloneNode(true))
            : [];
        }

        if (!this._originalThumbnailsHTML) {
          this._originalThumbnailsHTML = thumbSlider ? thumbSlider.innerHTML : '';
        }

        if (!this._originalDescriptions) {
          this._originalDescriptions = Array.from(
            document.querySelectorAll('.product__description')
          ).map((el) => ({ el, html: el.innerHTML }));
        }
      }

      updateVariantData(variant, html, variantFeaturedMediaId) {
        const sectionId = this.dataset.section;
        const galleryUl = this.querySelector(`#Slider-Gallery-${sectionId}`);
        const thumbSlider = this.querySelector(`#Slider-Thumbnails-${sectionId}`);
        const sliderComponent = this.querySelector(`#GalleryViewer-${sectionId}`);

        const mainCounterTotal = this.querySelector(`#GalleryViewer-${sectionId} .slider-counter--total`);
        const mainCounterCurrent = this.querySelector(`#GalleryViewer-${sectionId} .slider-counter--current`);
        const thumbCounterTotal = this.querySelector(`#GalleryThumbnails-${sectionId} .slider-counter--total`);
        const thumbCounterCurrent = this.querySelector(`#GalleryThumbnails-${sectionId} .slider-counter--current`);

        const variantData = window.variantDataMap?.[String(variant?.id)];

        // --- Description swap (both paths) ---
        const descEls = document.querySelectorAll('.product__description');
        const variantDescStore = document.querySelector(`.variant-data-store[data-variant-id="${variant?.id}"]`);
        const shortDescHtml = variantDescStore ? variantDescStore.innerHTML : null;

        if (shortDescHtml) {
          descEls.forEach((el) => (el.innerHTML = shortDescHtml));
        } else if (html) {
          // Fall back to fetched HTML description if no variant-data-store entry
          const fetchedDesc = html.querySelector('.product__description');
          if (fetchedDesc) {
            descEls.forEach((el) => (el.innerHTML = fetchedDesc.innerHTML));
          }
        }

        const thumbSizes = [
          `(min-width: 1600px) calc((825 - 4rem) / 4)`,
          `(min-width: 990px) calc((55.0vw - 4rem) / 4)`,
          `(min-width: 750px) calc((100vw - 15rem) / 8)`,
          `calc((100vw - 8rem) / 3)`
        ].join(', ');

        // =============================================
        // PATH A: variantDataMap has custom album data
        // =============================================
        if (variantData && variantData.album_images?.length) {

          // --- Replace main gallery slides ---
          if (galleryUl) {
            galleryUl.querySelectorAll('li[data-media-id]').forEach((li) => li.remove());

            variantData.album_images.forEach((imageUrl, index) => {
              const fullMediaId = `${sectionId}-variant-data-${variant.id}-${index}`;
              const li = document.createElement('li');
              li.id = `Slide-${sectionId}-variant-data-${variant.id}-${index}`;
              li.className = 'product__media-item grid__item slider__slide' +
                (index === 0 ? ' is-active' : ' is-show');
              li.dataset.mediaId = fullMediaId;
              li.setAttribute('role', 'presentation');
              li.innerHTML = `
                <div class="product-media-container media-type-image media-fit-contain global-media-settings gradient" style="--ratio: 1.0; --preview-ratio: 1.0;">
                  <modal-opener class="product__modal-opener product__modal-opener--image" data-modal="#ProductModal-${sectionId}">
                    <span class="product__media-icon motion-reduce quick-add-hidden product__media-icon--hover" aria-hidden="true">
                      <span class="svg-wrapper"><svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-plus" viewBox="0 0 19 19"><path fill="currentColor" fill-rule="evenodd" d="M4.667 7.94a.5.5 0 0 1 .499-.501l5.534-.014a.5.5 0 1 1 .002 1l-5.534.014a.5.5 0 0 1-.5-.5" clip-rule="evenodd"></path><path fill="currentColor" fill-rule="evenodd" d="M7.926 4.665a.5.5 0 0 1 .501.498l.014 5.534a.5.5 0 1 1-1 .003l-.014-5.534a.5.5 0 0 1 .499-.501" clip-rule="evenodd"></path><path fill="currentColor" fill-rule="evenodd" d="M12.832 3.03a6.931 6.931 0 1 0-9.802 9.802 6.931 6.931 0 0 0 9.802-9.802M2.323 2.323a7.931 7.931 0 0 1 11.296 11.136l4.628 4.628a.5.5 0 0 1-.707.707l-4.662-4.662A7.932 7.932 0 0 1 2.323 2.323" clip-rule="evenodd"></path></svg></span>
                    </span>
                    <div class="loading__spinner hidden">
                      <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle></svg>
                    </div>
                    <div class="product__media media media--transparent">
                      <img
                        src="https:${imageUrl}"
                        alt="${variantData.title} image ${index + 1}"
                        srcset="https:${imageUrl}&width=246 246w, https:${imageUrl}&width=493 493w, https:${imageUrl}&width=600 600w, https:${imageUrl}&width=713 713w, https:${imageUrl}&width=823 823w, https:${imageUrl}&width=990 990w, https:${imageUrl}&width=1100 1100w, https:${imageUrl}&width=1206 1206w, https:${imageUrl}&width=1346 1346w, https:${imageUrl}&width=1426 1426w, https:${imageUrl}&width=1646 1646w, https:${imageUrl}&width=1946 1946w"
                        class="image-magnify-hover"
                        loading="${index === 0 ? 'eager' : 'lazy'}"
                        width="1946"
                        height="1946"
                        sizes="(min-width: 1600px) 825px, (min-width: 990px) calc(55.0vw - 10rem), (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw / 1 - 4rem)"
                      />
                    </div>
                    <button class="product__media-toggle quick-add-hidden product__media-zoom-hover" type="button" aria-haspopup="dialog" data-media-id="variant-data-${variant.id}-${index}">
                      <span class="visually-hidden">
                        Open media ${index + 1} in modal
                      </span>
                    </button>
                  </modal-opener>
                </div>
              `;
              galleryUl.appendChild(li);
            });

            if (mainCounterTotal) mainCounterTotal.textContent = variantData.album_images.length;
            if (mainCounterCurrent) mainCounterCurrent.textContent = '1';
            galleryUl.scrollTo({ left: 0, behavior: 'smooth' });
          }

          // --- Replace modal content ---
          const modalContent = document.querySelector(`#ProductModal-${sectionId} .product-media-modal__content`);
          if (modalContent) {
            modalContent.innerHTML = variantData.album_images.map((imageUrl, index) => `
              <div class="product-media-modal__content-item" data-media-id="variant-data-${variant.id}-${index}">
                <img
                  src="https:${imageUrl}&width=1946"
                  alt="${variantData.title} image ${index + 1}"
                  loading="${index === 0 ? 'eager' : 'lazy'}"
                  width="1946"
                  height="1946"
                />
              </div>
            `).join('');
          }

          // --- Replace thumbnail strip ---
          if (thumbSlider) {
            thumbSlider.querySelectorAll('.slider-item').forEach((el) => el.remove());

            variantData.album_images.forEach((imageUrl, index) => {
              const fullMediaId = `${sectionId}-variant-data-${variant.id}-${index}`;
              const thumbId = `Thumbnail-${sectionId}-vd-${index}`;
              const slideId = `Slide-Thumbnails-${sectionId}-vd-${index}`;

              const div = document.createElement('div');
              div.id = slideId;
              div.className = 'slider-item';
              div.dataset.target = fullMediaId;
              div.dataset.mediaId = fullMediaId;
              div.dataset.mediaPosition = index + 1;
              div.style.height = '129px';

              div.innerHTML = `
                <button
                  class="thumbnail global-media-settings global-media-settings--no-shadow"
                  aria-label="Load image ${index + 1} in gallery view"
                  ${index === 0 ? 'aria-current="true"' : ''}
                  aria-controls="GalleryViewer-${sectionId}"
                  aria-describedby="${thumbId}"
                >
                  <img
                    src="https:${imageUrl}"
                    alt="${variantData.title} thumbnail ${index + 1}"
                    loading="lazy"
                    width="416"
                    height="416"
                    id="${thumbId}"
                    sizes="${thumbSizes}"
                  />
                </button>
              `;

              div.querySelector('button').addEventListener('click', () => {
                thumbSlider.querySelectorAll('.slider-item button').forEach((btn) => {
                  btn.removeAttribute('aria-current');
                });
                div.querySelector('button').setAttribute('aria-current', 'true');

                const targetSlide = galleryUl.querySelector(`[data-media-id="${fullMediaId}"]`);
                if (targetSlide) {
                  galleryUl.scrollTo({ left: targetSlide.offsetLeft, behavior: 'smooth' });
                  galleryUl.querySelectorAll('li[data-media-id]').forEach((li) => li.classList.remove('is-active'));
                  targetSlide.classList.add('is-active');
                }
              });

              thumbSlider.appendChild(div);
            });

            if (thumbCounterTotal) thumbCounterTotal.textContent = variantData.album_images.length;
            if (thumbCounterCurrent) thumbCounterCurrent.textContent = '1';
            thumbSlider.scrollTo({ top: 0, behavior: 'smooth' });
          }

        }
        // =============================================
        // PATH B: No variantDataMap — Dawn reconciliation from fetched HTML
        // =============================================
        else if (html) {

          // --- Thumbnail sync from fetched HTML ---
          const thumbSliderSource = html.querySelector(`#Slider-Thumbnails-${sectionId}`);
          this.updateThumbnails(thumbSliderSource, sectionId);

          // --- Gallery reconciliation (Dawn's approach) ---
          const mediaGallerySource = this.querySelector('media-gallery ul');
          const mediaGalleryDestination = html.querySelector('media-gallery ul');

          const refreshSourceData = () => {
            if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
            const items = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
            const set = new Set(items.map((item) => item.dataset.mediaId));
            const map = new Map(items.map((item, index) => [item.dataset.mediaId, { item, index }]));
            return [items, set, map];
          };

          if (mediaGallerySource && mediaGalleryDestination) {
            let [sourceItems, sourceSet, sourceMap] = refreshSourceData();
            const destItems = Array.from(mediaGalleryDestination.querySelectorAll('li[data-media-id]'));
            const destSet = new Set(destItems.map(({ dataset }) => dataset.mediaId));
            let shouldRefresh = false;

            // Pass 1: Add items from destination that are missing in source
            for (let i = destItems.length - 1; i >= 0; i--) {
              if (!sourceSet.has(destItems[i].dataset.mediaId)) {
                mediaGallerySource.prepend(destItems[i]);
                shouldRefresh = true;
              }
            }

            // Pass 2: Remove items from source that aren't in destination
            for (let i = 0; i < sourceItems.length; i++) {
              if (!destSet.has(sourceItems[i].dataset.mediaId)) {
                sourceItems[i].remove();
                shouldRefresh = true;
              }
            }

            if (shouldRefresh) [sourceItems, sourceSet, sourceMap] = refreshSourceData();

            // Pass 3: Reorder source to match destination order
            destItems.forEach((destItem, destIndex) => {
              const sourceData = sourceMap.get(destItem.dataset.mediaId);
              if (sourceData && sourceData.index !== destIndex) {
                mediaGallerySource.insertBefore(
                  sourceData.item,
                  mediaGallerySource.querySelector(`li:nth-of-type(${destIndex + 1})`)
                );
                [sourceItems, sourceSet, sourceMap] = refreshSourceData();
              }
            });

            // Update counters after reconciliation
            const newCount = mediaGallerySource.querySelectorAll('li[data-media-id]').length;
            if (mainCounterTotal) mainCounterTotal.textContent = newCount;
            if (mainCounterCurrent) mainCounterCurrent.textContent = '1';
          }

          // --- Set active media to variant's featured image ---
          if (variantFeaturedMediaId) {
            this.querySelector('media-gallery')?.setActiveMedia?.(
              `${sectionId}-${variantFeaturedMediaId}`,
              true
            );
          }

          // --- Modal sync (full replacement from fetched HTML) ---
          const modalContent = this.productModal?.querySelector('.product-media-modal__content');
          const newModalContent = html.querySelector('product-modal .product-media-modal__content');
          if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
        }
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        console.log("selectedVariant", selectedVariant);
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        const urlParams = new URLSearchParams(window.location.search);
        const viewParam = urlParams.get('view');

        if (viewParam) {
          params.push(`view=${viewParam}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            const corrected = this.autoCorrectInvalidSelection();
            if (corrected) return; 
            this.setUnavailable();
            return;
          }
          
          const onetimeExtraDiscount = document.getElementById("one_time_temporary_extra_discount")?.value;
          const tempExtraDiscount = document.getElementById("temp_extra_discount")?.value;

          const compareAtPriceCents = variant?.selling_plan_allocations?.[0]?.compare_at_price || variant?.compare_at_price;
          const variantPriceCents = variant?.selling_plan_allocations?.[0]?.price || variant?.price;

          const compareAtPrice = compareAtPriceCents / 100;
          const variantPrice = variantPriceCents / 100;

          const updateSubscriptionPrice = (comparePrice, discountedPrice) => {
            if (document.getElementById("xcompare-price")) {
              document.getElementById("xcompare-price").innerText = `$${comparePrice.toFixed(2)}`;
            }
            if (document.getElementById("xselling-sub-price")) {
              document.getElementById("xselling-sub-price").innerText = `$${discountedPrice.toFixed(2)}`;
            }
          };

          const updateOnetimePrice = (comparePrice, discountedPrice) => {
            if (document.getElementById("onetimeprice")) {
              document.getElementById("onetimeprice").innerText = `$${comparePrice.toFixed(2)}`;
            }
            if (document.getElementById("discounted-price")) {
              document.getElementById("discounted-price").innerText = `$${discountedPrice.toFixed(2)}`;
            }
          };

          let oneTimeDiscountedPrice = compareAtPrice;
          if (onetimeExtraDiscount) {
            const newPrice = (compareAtPrice * onetimeExtraDiscount) / 100;
            oneTimeDiscountedPrice = compareAtPrice - newPrice;
            oneTimeDiscountedPrice = Math.round(oneTimeDiscountedPrice * 100) / 100;
          } else {
            oneTimeDiscountedPrice = null;
          }

          let subscriptionCompareAtPrice = compareAtPrice;
          if (tempExtraDiscount) {
            const newPrice = (compareAtPrice * tempExtraDiscount) / 100;
            subscriptionCompareAtPrice = compareAtPrice - newPrice;
            subscriptionCompareAtPrice = Math.round(subscriptionCompareAtPrice * 100) / 100;
          } else {
            subscriptionCompareAtPrice = variantPrice;
          }

          updateSubscriptionPrice(compareAtPrice, subscriptionCompareAtPrice);
          updateOnetimePrice(compareAtPrice, oneTimeDiscountedPrice);

          this.pickupAvailability?.update(variant);
          

          // Use variant.id from fetched HTML — this is always the correct selected variant
          const hasVariantData = window.variantDataMap?.[String(variant?.id)];

          console.log("hasVariantData", hasVariantData);
          this.updateVariantData(variant, html, variant?.featured_media?.id);
          // // Reset descriptions
          //   if (this._originalDescriptions?.length) {
          //     this._originalDescriptions.forEach(({ el, html }) => {
          //       if (document.contains(el)) el.innerHTML = html;
          //     });
          //   }
          // if (hasVariantData) 
          // {
          //   this.updateVariantData(variant);
          // } 
          // else 
          // {
          //   // this.updateMedia(html, variant?.featured_media?.id);
          // }

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          const submitButton = document.getElementById(`ProductSubmitButton-${this.dataset.section}`);
          if (submitButton) {
            const isDisabled = submitButton?.hasAttribute('disabled') ?? true;
            submitButton.style.display = isDisabled ? "none" : "block";
          }

          if (window.initFlavorSlider) {
            setTimeout(window.initFlavorSlider, 100);
          }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        const urlParams = new URLSearchParams(window.location.search);
        const viewParam = urlParams.get('view');

        const params = [];
        if (variantId) params.push(`variant=${variantId}`);
        if (viewParam) params.push(`view=${viewParam}`);

        const newUrl = `${url}${params.length ? '?' + params.join('&') : ''}`;

        this.querySelector('share-button')?.updateUrl(`${window.shopUrl}${newUrl}`);

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', newUrl);
      }

      autoCorrectInvalidSelection() {
        const variantSelects = this.variantSelectors;
        if (!variantSelects) return false;

        const invalidFieldset = Array.from(variantSelects.querySelectorAll('fieldset')).find((fieldset) => {
          const checked = fieldset.querySelector('input:checked');
          return checked && checked.classList.contains('disabled');
        });

        if (!invalidFieldset) return false;

        const nextAvailable = invalidFieldset.querySelector('input:not(.disabled)');
        if (!nextAvailable) return false;

        nextAvailable.checked = true;
        nextAvailable.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const submitButton = document.getElementById(`ProductSubmitButton-${this.dataset.section}`);
        if (submitButton) submitButton.style.display = "block";

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateThumbnails(thumbSliderSource, sectionId) {
        const thumbSlider = this.querySelector(`#Slider-Thumbnails-${sectionId}`);
        if (!thumbSlider || !thumbSliderSource) return;

        thumbSlider.innerHTML = thumbSliderSource.innerHTML;

        thumbSlider.querySelectorAll('[data-target]').forEach((item) => {
          item.querySelector('button')?.addEventListener('click', () => {
            const target = item.dataset.target;
            thumbSlider.querySelectorAll('.slider-item button').forEach((b) => {
              b.removeAttribute('aria-current');
            });
            item.querySelector('button').setAttribute('aria-current', 'true');
            this.querySelector('media-gallery')?.setActiveMedia?.(target, false);
          });
        });

        const thumbCounterTotal = this.querySelector(`#GalleryThumbnails-${sectionId} .slider-counter--total`);
        const thumbCounterCurrent = this.querySelector(`#GalleryThumbnails-${sectionId} .slider-counter--current`);
        const count = thumbSlider.querySelectorAll('.slider-item').length;
        if (thumbCounterTotal) thumbCounterTotal.textContent = count;
        if (thumbCounterCurrent) thumbCounterCurrent.textContent = '1';

        thumbSlider.scrollTo({ top: 0, behavior: 'smooth' });
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const sectionId = this.dataset.section;
        const thumbSliderSource = html.querySelector(`#Slider-Thumbnails-${sectionId}`);
        this.updateThumbnails(thumbSliderSource, sectionId);

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);
            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        

        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}